import { usePublicLanguage } from '../public-site/PublicLanguage';
import React, { useEffect } from 'react';
import PublicLayout, { PublicThemeProps } from '../public-site/PublicLayout';
import ArticleCards from '../public-site/ArticleCards';
import { BlogPost, articleDate } from '../public-site/blog';
import { STUDY_URL } from '../public-site/routes';
import GraphicTextEffect from '../components/GraphicTextEffect/GraphicTextEffect';
import { Box, Typography } from '@mui/material';

/** Keep old hash-based blog bookmarks working. */
export function Blog() {
  const { t, href } = usePublicLanguage();
  useEffect(() => {
    window.location.replace('/blog/');
  }, []);
  return (
    <p>
      <a href={href('/blog/')}>{t('Open the Revelationary blog')}</a>
    </p>
  );
}

export function BlogPage({
  posts,
  post,
  missing = false,
  ...theme
}: PublicThemeProps & { posts: BlogPost[]; post?: BlogPost; missing?: boolean }) {
  const { t, href, locale } = usePublicLanguage();
  return (
    <PublicLayout {...theme}>
      {post ? (
        <article className="public-article home-width">
          <a className="home-text-link" href={href('/blog/')}>
            {t('← All articles')}
          </a>
          <header>
            <p className="home-eyebrow">
              {t('Bible study ·')}
              <time dateTime={post.publishedAt}>{articleDate(post.publishedAt, locale)}</time>
            </p>
            <h1 lang={post.contentLocale}>{post.title}</h1>
            {post.contentLocale === 'en' && locale !== 'en' && <p>{t('Available in English')}</p>}
            {post.author && (
              <p>
                {t('By')}
                {post.author}
              </p>
            )}
          </header>
          {post.image && <img className="article-cover" src={post.image} alt="" />}
          <div
            lang={post.contentLocale || locale}
            className="article-body"
            dangerouslySetInnerHTML={{ __html: post.html }}
          />
          <aside className="article-cta">
            <h2>{t('Take the question into scripture.')}</h2>
            <p>
              {t(
                'Read the passage, explore its words and join the discussion. Reading is open to everyone; commenting is free with an account.',
              )}
            </p>
            <a className="home-button" href={STUDY_URL}>
              {t('Open Bible study')}
            </a>
          </aside>
        </article>
      ) : (
        <section className="blog-index home-width">
          <h1>{missing ? t('Article not found') : '/blog'}</h1>
          <p className="home-eyebrow">{t('Read. Reflect. Discuss.')}</p>
          <p className="home-intro">
            {missing
              ? t(
                  'This article is not in our published collection. Explore the latest articles below.',
                )
              : t('Explore scripture and bring your questions back to the text.')}
          </p>
          {!missing && (
            <figure className="blog-faith-banner" aria-label="Jesus is King">
              <Box sx={{ border: '1px solid #a1a1a1' }}>
                <GraphicTextEffect id="svg-jesus" text="JESUS" />
              </Box>
              <Typography
                className="svg__sub-heading"
                component="figcaption"
                variant="h5"
                color="inherit"
                align="center"
                noWrap
                sx={{
                  width: '100%',
                  mt: 'calc(-5.833333% - 0.75em)',
                  mb: { xs: 8, md: 14 },
                  ml: 0,
                  fontSize: '0.9rem',
                  textDecoration: 'none',
                  color: '#a1a1a1',
                }}
              >
                IS KING
              </Typography>
            </figure>
          )}
          {posts.length ? (
            <>
              <h2>{t('Latest articles')}</h2>
              <ArticleCards posts={posts} />
            </>
          ) : (
            <div className="blog-empty">
              <h2>{t('A new chapter is coming.')}</h2>
              <p>
                {t(
                  'Our first articles will appear here when they are published. You can start exploring the Bible now.',
                )}
              </p>
              <a className="home-button" href={STUDY_URL}>
                {t('Open the Bible — free')}
              </a>
            </div>
          )}
        </section>
      )}
    </PublicLayout>
  );
}
